import React, { useEffect, useMemo, useState } from "react";
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from "./icons";
import { Skeleton } from "./Skeleton";
import EmptyState from "./EmptyState";
import Button from "./Button";

/**
 * The workhorse table (§2.7): sticky header, sortable columns, toolbar with
 * search-as-you-type + filter slot + count, pagination, skeleton loading,
 * designed empty state, whole-row click to detail.
 *
 * columns: [{
 *   key,            // unique id; also default row accessor
 *   header,         // column label
 *   render?,        // (row) => node — custom cell
 *   sortValue?,     // (row) => comparable — enables custom sort
 *   sortable?,      // default true when the column has a key accessor
 *   align?,         // "num" right-aligns with tabular numerals (money)
 *   width?
 * }]
 * rows: array of records. rowKey: (row) => stable key.
 * searchText: (row) => string searched by the toolbar input (omit to hide search).
 * countLabel: "clients" → "312 clients".
 */
function DataTable({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName, // optional (row) => extra class — e.g. deep-link highlight
  loading = false,
  pageSize = 50,
  searchText,
  searchPlaceholder = "Search…",
  countLabel = "rows",
  toolbarExtra,
  emptyState
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState(null); // { key, dir: 1 | -1 }
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    if (!searchText || !query.trim()) {
      return rows;
    }
    const q = query.trim().toLowerCase();
    return rows.filter((row) => searchText(row).toLowerCase().includes(q));
  }, [rows, query, searchText]);

  const sorted = useMemo(() => {
    if (!sort) {
      return filtered;
    }
    const col = columns.find((c) => c.key === sort.key);
    if (!col) {
      return filtered;
    }
    const accessor = col.sortValue || ((row) => row[col.key]);
    return [...filtered].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      if (va == null && vb == null) {
        return 0;
      }
      if (va == null) {
        return 1;
      } // nulls last regardless of direction
      if (vb == null) {
        return -1;
      }
      if (va < vb) {
        return -sort.dir;
      }
      if (va > vb) {
        return sort.dir;
      }
      return 0;
    });
  }, [filtered, sort, columns]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => {
    setPage(0);
  }, [query, sort, rows.length]);

  const pageRows = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const toggleSort = (col) => {
    if (col.sortable === false) {
      return;
    }
    setSort((current) =>
      current && current.key === col.key
        ? { key: col.key, dir: -current.dir }
        : { key: col.key, dir: 1 }
    );
  };

  const showToolbar = Boolean(searchText || toolbarExtra);

  return (
    <div className="fk-table-wrap">
      {showToolbar ? (
        <div className="fk-table-toolbar">
          {searchText ? (
            <div className="fk-table-toolbar__search">
              <span className="fk-table-toolbar__search-icon">
                <SearchIcon size={14} />
              </span>
              <input
                className="fk-input"
                type="search"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={searchPlaceholder}
              />
            </div>
          ) : null}
          {toolbarExtra}
          <span className="fk-table-toolbar__count">
            {loading ? "Loading…" : `${sorted.length} ${countLabel}`}
          </span>
        </div>
      ) : null}

      <div className="fk-table-scroll">
        <table className="fk-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const sortable = col.sortable !== false;
                const isSorted = sort && sort.key === col.key;
                return (
                  <th
                    key={col.key}
                    style={col.width ? { width: col.width } : undefined}
                    className={[
                      sortable ? "fk-table__th--sortable" : "",
                      col.align === "num" ? "fk-table__th--num" : ""
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-sort={isSorted ? (sort.dir === 1 ? "ascending" : "descending") : undefined}
                    onClick={sortable ? () => toggleSort(col) : undefined}
                  >
                    {col.header}
                    {isSorted ? (
                      <span className="fk-table__sort-arrow">{sort.dir === 1 ? "↑" : "↓"}</span>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`skeleton-${i}`}>
                    {columns.map((col) => (
                      <td key={col.key}>
                        <Skeleton height={12} width={col.align === "num" ? 56 : "70%"} />
                      </td>
                    ))}
                  </tr>
                ))
              : pageRows.map((row) => (
                  <tr
                    key={rowKey(row)}
                    className={
                      [
                        onRowClick ? "fk-table__row--clickable" : "",
                        rowClassName ? rowClassName(row) || "" : ""
                      ]
                        .filter(Boolean)
                        .join(" ") || undefined
                    }
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={col.align === "num" ? "fk-table__cell--num" : undefined}
                      >
                        {col.render ? col.render(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
        {!loading && sorted.length === 0
          ? emptyState || (
              <EmptyState
                title="Nothing here yet"
                message={
                  query
                    ? `No matches for “${query}” — check the spelling or clear the search.`
                    : `No ${countLabel} to show.`
                }
              />
            )
          : null}
      </div>

      {!loading && sorted.length > pageSize ? (
        <div className="fk-table-footer">
          <span>
            Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sorted.length)} of{" "}
            {sorted.length}
          </span>
          <span className="fk-table-footer__pager">
            <Button
              size="sm"
              variant="ghost"
              icon={<ChevronLeftIcon size={14} />}
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              aria-label="Previous page"
            />
            <span>
              Page {page + 1} of {pageCount}
            </span>
            <Button
              size="sm"
              variant="ghost"
              icon={<ChevronRightIcon size={14} />}
              disabled={page >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              aria-label="Next page"
            />
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default DataTable;
